// src/components/planner/modals/EditAbsenceModal.tsx

import React, { useState } from 'react';
import { X, Save, Trash2 } from 'lucide-react';

const API_BASE_URL = '/api';

interface EditAbsenceModalProps {
    absence: {
        _id: string;
        fixedEventId?: { _id: string; title: string; dayOfWeek: number; startTime: string };
        customEventName?: string;
        date: string;
        subject?: string;
        reason?: string;
        needsReview: boolean;
        reviewed: boolean;
    };
    events: any[]; // allEvents da grade semanal
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const EditAbsenceModal: React.FC<EditAbsenceModalProps> = ({
    absence,
    events,
    onClose,
    onSave,
    onShowToast
}) => {
    const [formData, setFormData] = useState({
        fixedEventId: absence.fixedEventId?._id || 'outro',
        customEvent: absence.customEventName || '',
        date: new Date(absence.date).toISOString().split('T')[0],
        subject: absence.subject || '',
        reason: absence.reason || '',
        needsReview: absence.needsReview,
    });

    const [confirmDelete, setConfirmDelete] = useState(false);

    const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.fixedEventId) {
            onShowToast('Selecione a aula', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/absences/${absence._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    fixedEventId: formData.fixedEventId === 'outro' ? undefined : formData.fixedEventId,
                    customEventName: formData.fixedEventId === 'outro' ? formData.customEvent : undefined,
                    date: formData.date,
                    reason: formData.reason || undefined,
                    subject: formData.subject || undefined,
                    needsReview: formData.needsReview
                })
            });

            if (response.ok) {
                onShowToast('Falta atualizada!', 'success');
                onSave();
            } else {
                const data = await response.json();
                onShowToast(data.error || 'Erro ao atualizar falta', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao atualizar falta', 'error');
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/absences/${absence._id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Falta removida', 'info');
                onSave();
            } else {
                onShowToast('Erro ao remover falta', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao remover falta', 'error');
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Editar Registro de Falta</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">

                        <div className="plnr-form-group">
                            <label>Qual aula você faltou? *</label>
                            <select
                                value={formData.fixedEventId}
                                onChange={(e) => setFormData({ ...formData, fixedEventId: e.target.value })}
                                className="plnr-select"
                                required
                            >
                                <option value="">Selecione a aula...</option>
                                {events.map(event => (
                                    <option key={event._id} value={event._id}>
                                        {event.title} — {DAY_NAMES[event.dayOfWeek]} {event.startTime?.substring(0, 5)}
                                    </option>
                                ))}
                                <option value="outro">Outro</option>
                            </select>

                            {formData.fixedEventId === 'outro' && (
                                <input
                                    type="text"
                                    value={formData.customEvent}
                                    onChange={(e) => setFormData({ ...formData, customEvent: e.target.value })}
                                    placeholder="Nome da aula que você faltou"
                                    className="plnr-input"
                                    style={{ marginTop: '8px' }}
                                />
                            )}
                        </div>

                        <div className="plnr-form-group">
                            <label>Data da aula *</label>
                            <input
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                className="plnr-input"
                                required
                            />
                        </div>

                        <div className="plnr-form-group">
                            <label>Assunto da aula (opcional)</label>
                            <input
                                type="text"
                                value={formData.subject}
                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                placeholder="Ex: Hipertensão Arterial"
                                className="plnr-input"
                            />
                        </div>

                        <div className="plnr-form-group">
                            <label>Motivo (opcional)</label>
                            <textarea
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                placeholder="Ex: Consulta médica, viagem..."
                                className="plnr-textarea"
                                rows={2}
                            />
                        </div>

                        <div className="plnr-absence-check">
                            <label className="plnr-absence-check-label">
                                <input
                                    type="checkbox"
                                    checked={formData.needsReview}
                                    onChange={(e) => setFormData({ ...formData, needsReview: e.target.checked })}
                                    className="plnr-absence-checkbox"
                                />
                                <span>Preciso revisar o conteúdo desta aula</span>
                            </label>
                        </div>

                        {/* CONFIRMAÇÃO DE EXCLUSÃO */}
                        {confirmDelete && (
                            <div style={{
                                padding: '12px',
                                background: 'rgba(220, 38, 38, 0.08)',
                                border: '1px solid rgba(220, 38, 38, 0.3)',
                                borderRadius: '8px',
                                marginTop: '12px'
                            }}>
                                <p style={{ fontSize: '13px', color: '#dc2626', margin: '0 0 10px 0', fontWeight: 600 }}>
                                    Excluir este registro de falta?
                                </p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button" className="plnr-btn danger" onClick={handleDelete}>
                                        <Trash2 size={14} />
                                        Sim, excluir
                                    </button>
                                    <button type="button" className="plnr-btn secondary" onClick={() => setConfirmDelete(false)}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="plnr-modal-footer" style={{ justifyContent: 'space-between' }}>
                        <button
                            type="button"
                            className="plnr-btn danger"
                            onClick={() => setConfirmDelete(true)}
                            style={{ opacity: confirmDelete ? 0.4 : 1 }}
                            disabled={confirmDelete}
                        >
                            <Trash2 size={14} />
                            Excluir
                        </button>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" className="plnr-btn secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button type="submit" className="plnr-btn primary">
                                <Save size={16} />
                                Salvar
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditAbsenceModal;